/**
 * Liest die Prüfberichte, die ein VBA-Prüfmakro in der Datei abgelegt hat.
 *
 * Ein Prüfbericht ist ein benutzerdefinierter XML-Teil (Custom XML Part) im
 * Namensraum `PRUEFBERICHT_NAMESPACE`:
 *
 * ```xml
 * <pruefbericht xmlns="urn:interaktiv-office-course:pruefbericht"
 *               pruefung="pp-uebergaenge" version="1" erstellt="2026-09-24T14:05:00">
 *   <wert name="folien">5</wert>
 *   <wert name="folie.1.effekt">3844</wert>
 * </pruefbericht>
 * ```
 *
 * Benutzerdefinierte XML-Teile sind in allen drei Anwendungen über Office.js
 * erreichbar – Word ab WordApi 1.4, Excel ab ExcelApi 1.5, PowerPoint ab
 * PowerPointApi 1.7. Genau das macht sie zur Brücke zwischen VBA und dem
 * Add-in: VBA kann sie schreiben, Office.js kann sie lesen.
 */

import type { OfficeHost } from "@/core/types";
import { isSetSupported } from "@/office/requirements";
import { PRUEFBERICHT_NAMESPACE } from "./vba";

/** Anforderungssatz, ab dem benutzerdefinierte XML-Teile lesbar sind. */
export const XML_TEILE: Record<OfficeHost, { satz: string; version: string }> = {
  Word: { satz: "WordApi", version: "1.4" },
  Excel: { satz: "ExcelApi", version: "1.5" },
  PowerPoint: { satz: "PowerPointApi", version: "1.7" },
};

/** Ein ausgelesener Prüfbericht mit bequemem Zugriff auf seine Werte. */
export class Pruefbericht {
  constructor(
    readonly pruefung: string,
    readonly erstellt: Date | null,
    readonly werte: ReadonlyMap<string, string>,
  ) {}

  /** Meldung, falls das Makro beim Auslesen auf einen Fehler gestoßen ist. */
  get fehler(): string | null {
    return this.werte.get("fehler") ?? null;
  }

  text(name: string): string | null {
    return this.werte.get(name) ?? null;
  }

  /** Zahlwert; das Makro schreibt Zahlen immer mit Punkt als Dezimaltrenner. */
  zahl(name: string): number | null {
    const roh = this.werte.get(name);
    if (roh === undefined || roh.trim() === "") return null;
    const wert = Number(roh.trim());
    return Number.isFinite(wert) ? wert : null;
  }

  /** Wahrheitswert; das Makro schreibt "1" oder "0". */
  janein(name: string): boolean | null {
    const roh = this.werte.get(name);
    if (roh === "1") return true;
    if (roh === "0") return false;
    return null;
  }

  /**
   * Alle Einträge einer nummerierten Gruppe, z. B. `folie.1.effekt`,
   * `folie.2.effekt` … Ergebnis: Nummer → (Feld → Wert).
   */
  gruppe(praefix: string): Map<number, Map<string, string>> {
    const ergebnis = new Map<number, Map<string, string>>();
    const muster = new RegExp(`^${praefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.(\\d+)\\.(.+)$`);
    for (const [name, wert] of this.werte) {
      const treffer = muster.exec(name);
      if (!treffer) continue;
      const nummer = Number(treffer[1]);
      const eintrag = ergebnis.get(nummer) ?? new Map<string, string>();
      eintrag.set(treffer[2], wert);
      ergebnis.set(nummer, eintrag);
    }
    return new Map([...ergebnis.entries()].sort(([a], [b]) => a - b));
  }
}

export type BerichtErgebnis =
  | { status: "vorhanden"; bericht: Pruefbericht }
  | { status: "fehlt" }
  | { status: "nicht-lesbar"; satz: string; version: string };

/** Zerlegt das XML eines Prüfberichts. Ohne Office.js testbar. */
export function berichtAusXml(xml: string): Pruefbericht | null {
  const dokument = new DOMParser().parseFromString(xml, "application/xml");
  const wurzel = dokument.documentElement;
  if (!wurzel || wurzel.localName !== "pruefbericht") return null;
  if (dokument.getElementsByTagName("parsererror").length > 0) return null;

  const werte = new Map<string, string>();
  const eintraege = wurzel.getElementsByTagNameNS(PRUEFBERICHT_NAMESPACE, "wert");
  for (let i = 0; i < eintraege.length; i++) {
    const eintrag = eintraege[i];
    const name = eintrag.getAttribute("name");
    if (name) werte.set(name, eintrag.textContent ?? "");
  }

  const erstelltRoh = wurzel.getAttribute("erstellt");
  const erstellt = erstelltRoh ? new Date(erstelltRoh) : null;

  return new Pruefbericht(
    wurzel.getAttribute("pruefung") ?? "",
    erstellt && !Number.isNaN(erstellt.getTime()) ? erstellt : null,
    werte,
  );
}

async function xmlTeileLesen(host: OfficeHost): Promise<string[]> {
  switch (host) {
    case "Word":
      return Word.run(async (context) => {
        const teile = context.document.customXmlParts.getByNamespace(PRUEFBERICHT_NAMESPACE);
        teile.load("items");
        await context.sync();
        const inhalte = teile.items.map((teil) => teil.getXml());
        await context.sync();
        return inhalte.map((inhalt) => inhalt.value);
      });
    case "Excel":
      return Excel.run(async (context) => {
        const teile = context.workbook.customXmlParts.getByNamespace(PRUEFBERICHT_NAMESPACE);
        teile.load("items");
        await context.sync();
        const inhalte = teile.items.map((teil) => teil.getXml());
        await context.sync();
        return inhalte.map((inhalt) => inhalt.value);
      });
    case "PowerPoint":
      return PowerPoint.run(async (context) => {
        const teile = context.presentation.customXmlParts.getByNamespace(PRUEFBERICHT_NAMESPACE);
        teile.load("items");
        await context.sync();
        const inhalte = teile.items.map((teil) => teil.getXml());
        await context.sync();
        return inhalte.map((inhalt) => inhalt.value);
      });
  }
}

/**
 * Sucht den neuesten Prüfbericht einer Prüfung in der geöffneten Datei.
 */
export async function readPruefbericht(host: OfficeHost, pruefung: string): Promise<BerichtErgebnis> {
  const { satz, version } = XML_TEILE[host];
  if (!isSetSupported(satz, version)) return { status: "nicht-lesbar", satz, version };

  const berichte = (await xmlTeileLesen(host))
    .map(berichtAusXml)
    .filter((bericht): bericht is Pruefbericht => bericht !== null && bericht.pruefung === pruefung)
    .sort((a, b) => (b.erstellt?.getTime() ?? 0) - (a.erstellt?.getTime() ?? 0));

  return berichte.length > 0 ? { status: "vorhanden", bericht: berichte[0] } : { status: "fehlt" };
}
